import { useMemo } from 'react'
import { Field } from 'formik'
import classNames from 'classnames'
import { isValidBookingPhone } from 'utils/phone'
import _ from 'lodash'
import DatePicker from 'react-datepicker'
import InputPhone from 'components/atoms/PhoneInput'
import Selection from 'components/molecules/Selection'
import { BOOKING_CLUSTER, BOOKING_SECTION_TITLE, CREATE_BOOKING_FIELD, CREATE_BOOKING_SELECTION } from '../../bookingUiTokens'
import {
  clampCompanyBookingDateTime,
  clampCompanyBookingEndAfterStart,
  endOfToday,
  filterBookingEndTime,
  filterBookingStartTime,
  getMinTimeForDate,
  needsCompanyEndDateClamp,
  needsCompanyStartDateClamp,
  startOfToday,
} from '../bookingDateUtils'

const LocationStep = ({
  t,
  values,
  errors,
  touched,
  setFieldValue,
  formikRef,
  hasAdminAccess,
  isCompanyBookingFlow,
  userOptions,
  fetchUsers,
  isArabic,
  datePickerLocale,
  selectDateTimePlaceholder,
  clickToPickLocationPlaceholder,
  clickToPickPickupPlaceholder,
  clickToPickDropoffPlaceholder,
  openLocationModal,
  openPickupLocationModal,
  openDropoffLocationModal,
  onDeliveryMethodChange,
}) => {
  const blockPastDates = isCompanyBookingFlow
  const isDeliver = values.deliveryMethod === 'deliver'
  const startDayKey = values.rentalStartDate ? new Date(values.rentalStartDate).toDateString() : ''
  const endDayKey = values.rentalEndDate ? new Date(values.rentalEndDate).toDateString() : values.rentalStartDate ? new Date(values.rentalStartDate).toDateString() : ''

  const minStartDate = useMemo(() => (blockPastDates ? startOfToday() : undefined), [blockPastDates])
  const minEndDate = useMemo(() => (blockPastDates ? values.rentalStartDate || startOfToday() : undefined), [blockPastDates, values.rentalStartDate])
  const startMinTime = useMemo(() => (blockPastDates ? getMinTimeForDate(values.rentalStartDate) : undefined), [blockPastDates, startDayKey])
  const startMaxTime = useMemo(() => (blockPastDates ? endOfToday() : undefined), [blockPastDates])
  const endMinTime = useMemo(() => (blockPastDates ? getMinTimeForDate(values.rentalEndDate || values.rentalStartDate) : undefined), [blockPastDates, endDayKey])
  const endMaxTime = useMemo(() => (blockPastDates ? endOfToday() : undefined), [blockPastDates])

  const handleDeliverSelect = () => {
    onDeliveryMethodChange('deliver', setFieldValue)
  }

  const handlePickupSelect = () => {
    onDeliveryMethodChange('pickup', setFieldValue)
    setFieldValue('pickupLocation', '')
    setFieldValue('dropoffLocation', '')
    setFieldValue('pickupLat', '')
    setFieldValue('pickupLon', '')
    setFieldValue('dropoffLat', '')
    setFieldValue('dropoffLon', '')
  }

  return (
    <div className="space-y-8 pt-2">
      <div>
        <div className={BOOKING_SECTION_TITLE}>{t('bookingProfile.sections.customerAndDates')}</div>
        <div className="mt-4 space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {hasAdminAccess && !isCompanyBookingFlow && (
              <div className="min-w-0">
                <label htmlFor="selectedUser" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-black">
                  {t('bookingProfile.labels.users')}
                </label>
                <Selection
                  onChange={value => {
                    if (value && value.id) {
                      setFieldValue('selectedUser', value.id)
                    }
                  }}
                  title={t('bookingProfile.placeholders.selectUser')}
                  list={userOptions}
                  value={values.selectedUser}
                  showSearch
                  onSearch={searchQuery => fetchUsers(searchQuery)}
                  className={CREATE_BOOKING_SELECTION}
                />
              </div>
            )}
            <div className={classNames('min-w-0', isCompanyBookingFlow && 'md:col-span-2')}>
              <label htmlFor="phone" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-black">
                {t('bookingProfile.labels.phone')}
              </label>
              <Field name="phone">
                {({ field, form }) => {
                  const phoneRequired = isCompanyBookingFlow || !values.selectedUser
                  const showPhoneError = Boolean(errors.phone && touched.phone) || (phoneRequired && field.value && touched.phone && !isValidBookingPhone(field.value))
                  const phoneErrorMessage = errors.phone || (phoneRequired && field.value && !isValidBookingPhone(field.value) ? t('bookingProfile.validation.phoneInvalid') : null)

                  return (
                    <>
                      <InputPhone
                        id="phone"
                        value={field.value || undefined}
                        onChange={value => {
                          form.setFieldValue('phone', value || '')
                          if (touched.phone) {
                            form.validateField('phone')
                          }
                        }}
                        onBlur={e => {
                          field.onBlur(e)
                          form.setFieldTouched('phone', true, false)
                          form.validateField('phone')
                        }}
                        defaultCountry="OM"
                        placeholder={t('bookingProfile.placeholders.phone')}
                        inputClassName={classNames(CREATE_BOOKING_FIELD, showPhoneError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20')}
                      />
                      {showPhoneError && phoneErrorMessage && <div className="mt-1 text-sm text-red-500">{phoneErrorMessage}</div>}
                    </>
                  )
                }}
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="min-w-0" dir={isArabic ? 'rtl' : 'ltr'}>
              <label htmlFor="rentalStartDate" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-black">
                {t('bookingProfile.labels.bookingFrom')}
              </label>
              <DatePicker
                selected={values.rentalStartDate}
                onChange={date => {
                  let nextDate = date
                  if (blockPastDates && date && needsCompanyStartDateClamp(date)) {
                    nextDate = clampCompanyBookingDateTime(date)
                  }
                  setFieldValue('rentalStartDate', nextDate, false)
                  if (blockPastDates && nextDate && values.rentalEndDate && new Date(values.rentalEndDate).getTime() <= new Date(nextDate).getTime()) {
                    setFieldValue('rentalEndDate', null, false)
                  }
                  setTimeout(() => {
                    formikRef.current.validateField('rentalStartDate')
                    formikRef.current.validateField('rentalEndDate')
                  }, 0)
                }}
                onBlur={() => {
                  formikRef.current.setFieldTouched('rentalStartDate', true, false)
                  formikRef.current.validateField('rentalStartDate')
                  formikRef.current.validateField('rentalEndDate')
                }}
                showTimeSelect
                {...(blockPastDates && {
                  minDate: minStartDate,
                  minTime: startMinTime,
                  maxTime: startMaxTime,
                  filterTime: time => filterBookingStartTime(time, values.rentalStartDate),
                })}
                dateFormat="MMMM d, yyyy h:mm aa"
                locale={datePickerLocale}
                id="rentalStartDate"
                className={CREATE_BOOKING_FIELD}
                placeholderText={selectDateTimePlaceholder}
              />
              {errors.rentalStartDate && touched.rentalStartDate && <div className="mt-1 text-sm text-red-500">{errors.rentalStartDate}</div>}
            </div>

            <div className="min-w-0" dir={isArabic ? 'rtl' : 'ltr'}>
              <label htmlFor="rentalEndDate" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-black">
                {t('bookingProfile.labels.bookingTo')}
              </label>
              <DatePicker
                selected={values.rentalEndDate}
                onChange={date => {
                  let nextDate = date
                  if (blockPastDates && date && values.rentalStartDate && needsCompanyEndDateClamp(date, values.rentalStartDate)) {
                    nextDate = clampCompanyBookingEndAfterStart(date, values.rentalStartDate)
                  }
                  setFieldValue('rentalEndDate', nextDate, false)
                  setTimeout(() => {
                    formikRef.current.setFieldTouched('rentalEndDate', true, false)
                    formikRef.current.validateField('rentalEndDate')
                  }, 0)
                }}
                onBlur={() => {
                  formikRef.current.setFieldTouched('rentalEndDate', true, false)
                  formikRef.current.validateField('rentalEndDate')
                }}
                showTimeSelect
                {...(minEndDate && { minDate: minEndDate })}
                {...(blockPastDates && {
                  minTime: endMinTime,
                  maxTime: endMaxTime,
                  filterTime: time => filterBookingEndTime(time, values.rentalStartDate, values.rentalEndDate),
                })}
                dateFormat="MMMM d, yyyy h:mm aa"
                locale={datePickerLocale}
                id="rentalEndDate"
                className={CREATE_BOOKING_FIELD}
                placeholderText={selectDateTimePlaceholder}
              />
              {errors.rentalEndDate && touched.rentalEndDate && <div className="mt-1 text-sm text-red-500">{errors.rentalEndDate}</div>}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className={BOOKING_SECTION_TITLE}>{t('bookingProfile.labels.deliveryQuestion')}</div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="deliver"
            className={classNames(
              'flex cursor-pointer gap-3 rounded-2xl border-2 p-4 transition-all',
              isDeliver ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
            )}
          >
            <input id="deliver" name="deliveryMethod" type="radio" className="mt-1 h-4 w-4 text-dark-600 focus:ring-dark-500" checked={isDeliver} onChange={handleDeliverSelect} />
            <div>
              <div className="text-sm font-bold text-slate-900">{t('bookingProfile.labels.deliverToMe')}</div>
              <p className="mt-0.5 text-xs text-slate-500">{t('bookingProfile.labels.deliverToMeHint')}</p>
            </div>
          </label>

          <label
            htmlFor="pickup"
            className={classNames(
              'flex cursor-pointer gap-3 rounded-2xl border-2 p-4 transition-all',
              values.deliveryMethod === 'pickup' ? 'border-dark-600 bg-dark-600/5 ring-2 ring-dark-600/20' : 'border-slate-200/80 bg-white hover:border-slate-300'
            )}
          >
            <input
              id="pickup"
              name="deliveryMethod"
              type="radio"
              className="mt-1 h-4 w-4 text-dark-600 focus:ring-dark-500"
              checked={values.deliveryMethod === 'pickup'}
              onChange={handlePickupSelect}
            />
            <div>
              <div className="text-sm font-bold text-slate-900">{t('bookingProfile.labels.pickupMyself')}</div>
              <p className="mt-0.5 text-xs text-slate-500">{t('bookingProfile.labels.pickupMyselfHint')}</p>
            </div>
          </label>
        </div>
        {errors.deliveryMethod && touched.deliveryMethod && <div className="mt-2 text-sm text-red-500">{errors.deliveryMethod}</div>}
      </div>

      <div>
        <div className={BOOKING_SECTION_TITLE}>{isDeliver ? t('bookingProfile.sections.deliveryLocations') : t('bookingProfile.sections.pickupLocation')}</div>
        <div className="mt-4 space-y-5">
          {!isDeliver && (
            <div className="min-w-0">
              <label htmlFor="location" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-black">
                {t('bookingProfile.labels.location')}
              </label>
              <div className="min-w-0" onClick={openLocationModal} role="presentation">
                <input
                  type="text"
                  id="location"
                  value={values.address || ''}
                  readOnly
                  placeholder={clickToPickLocationPlaceholder}
                  className={`${CREATE_BOOKING_FIELD} cursor-pointer`}
                  onClick={openLocationModal}
                />
                <div className="mt-1.5 text-xs text-slate-500">
                  {values.lat && values.lon ? (
                    <span>
                      Lat: {values.lat}, Lon: {values.lon}
                    </span>
                  ) : (
                    <span className="text-slate-400">{t('bookingProfile.messages.noCoordinatesSelected')}</span>
                  )}
                </div>
              </div>
              {errors.lat && touched.lat && <div className="mt-1 text-sm text-red-500">{errors.lat}</div>}
            </div>
          )}

          {isDeliver && (
            <div className={classNames(BOOKING_CLUSTER, 'space-y-6')}>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-black">{t('bookingProfile.labels.pickupLocationTitle')}</div>
                <div className="mt-3" onClick={openPickupLocationModal} role="presentation">
                  <input
                    type="text"
                    value={values.pickupLocation || ''}
                    readOnly
                    placeholder={clickToPickPickupPlaceholder}
                    className={`${CREATE_BOOKING_FIELD} cursor-pointer`}
                    onClick={openPickupLocationModal}
                  />
                  <div className="mt-1.5 text-xs text-slate-500">
                    {values.pickupLat && values.pickupLon ? (
                      <span>
                        Lat: {values.pickupLat}, Lon: {values.pickupLon}
                      </span>
                    ) : (
                      <span className="text-slate-400">{t('bookingProfile.messages.noCoordinatesSelected')}</span>
                    )}
                  </div>
                </div>
                {errors.pickupLat && touched.pickupLat && <div className="mt-1 text-sm text-red-500">{errors.pickupLat}</div>}
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-black">{t('bookingProfile.labels.dropoffLocationTitle')}</div>
                <div className="mt-3" onClick={openDropoffLocationModal} role="presentation">
                  <input
                    type="text"
                    value={values.dropoffLocation || ''}
                    readOnly
                    placeholder={clickToPickDropoffPlaceholder}
                    className={`${CREATE_BOOKING_FIELD} cursor-pointer`}
                    onClick={openDropoffLocationModal}
                  />
                  <div className="mt-1.5 text-xs text-slate-500">
                    {values.dropoffLat && values.dropoffLon ? (
                      <span>
                        Lat: {values.dropoffLat}, Lon: {values.dropoffLon}
                      </span>
                    ) : (
                      <span className="text-slate-400">{t('bookingProfile.messages.noCoordinatesSelected')}</span>
                    )}
                  </div>
                </div>
                {errors.dropoffLat && touched.dropoffLat && <div className="mt-1 text-sm text-red-500">{errors.dropoffLat}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LocationStep
